import _, { reduce } from 'lodash';
import { AccountType } from '@/common/constants';
import { flowTypes } from '@/common/service/page-config';
import { isEmptyVal, safeJSONParse } from '@/utils';
import { couponSourceTypeMap } from '@/views/config/coupon/helper/constant';
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';
import { logger } from '@/utils/log';
import { BillingProductEntry } from "@zoom/billing-extensions";

const getCurrentFlowType = (config = {}) => {
  const accountType = config?.accountType;
  const isFreeAccount = [AccountType.ACCOUNT_FREE_WITH_CC, AccountType.ACCOUNT_FREE_WITHOUT_CC].includes(accountType);
  // todo with paid
  return config?.pageConfig?.type || (isFreeAccount ? flowTypes.basic2pro : flowTypes.buyNow);
};

/**
 *
 * @param planCodeList
 * @param defaultValue
 * @return {PreloadDiscountMapBean}
 */
export const transferPlanCodeListToDiscountInfo = (planCodeList = [], defaultValue = {}) => {
  return reduce(planCodeList, (res, planCode) => {
    if (planCode) res[planCode] = { ...defaultValue, planCode };
    return res;
  }, {});
};

// pre config preload coupon data with newBillingConfig
export const preConfigWithCampaign = (config = {}) => {
  if (!isEmptyVal(config.marketingCoupon)) {
    const couponData = safeJSONParse(config.marketingCoupon);

    if (couponData) {
      const { couponSourceType, supportPlanCodeList = [], defaultPlanCodeList = [] } = couponData || {};

      if (couponSourceType === couponSourceTypeMap.threshold) {
        // transfer supportPlanCodeList to discountInfo object
        couponData.discountInfo = transferPlanCodeListToDiscountInfo(supportPlanCodeList);
      }

      couponData.defaultDiscountInfo = transferPlanCodeListToDiscountInfo(defaultPlanCodeList);
    }

    const pageConfig = { ...config?.pageConfig, type: getCurrentFlowType(config) };

    return {
      ...config,
      pageConfig,
      marketingCoupon: couponData,
    };
  }

  if (!isEmptyVal(config.trialCampaignInfo?.campaignList)) {
    const { campaignList } = config.trialCampaignInfo;

    const result = BillingProductEntry.syncPreCheckForProductEntry({entryOptions: campaignList[0]});

    // generate current free trial purchase flow
    if (result?.pageQueryConfig) {
      config.pageConfig = {
        ...config.pageConfig,
        ...result?.pageQueryConfig,
      }
    }
  }

  return config;
};

export const preConfigWithCartId = (config = {}) => {
  /** @type {PreloadWithCartIdResultDto} */
  const preloadCartInfo = config?.preloadCartInfo;

  if (!isEmptyVal(preloadCartInfo)) {
    const pageConfig = {
      ...config?.pageConfig, ...(preloadCartInfo.configData || {}),
      type: getCurrentFlowType(config),
    };

    return {
      ...config,
      pageConfig,
    };
  }

  return config;
};

/**
 * @param config{GlobalPageConfigBean}
 */
export const preConfigWithPartnerRefer = (config = {}) => {
  /**
   * @description replace config plan map price for landing page
   * @param p {PartnerReferBean}
   */
  function replacePartnerPlanPrice(p = {}) {
    if (isEmptyVal(p?.planPrice)) return;

    p.planPrice = _.chain(p.planPrice)
      .keys()
      .reduce((pre, planCode) => {
        const currentPlan = _.get(p.planPrice, planCode, {});
        const matchPlanCodeGroupList = PLAN_PRICE_CHANGE_UTILS.getPlanListForPriceChange(planCode);

        if (matchPlanCodeGroupList) {
          return _.reduce(matchPlanCodeGroupList, (v, p) => {
            return { ...v, [p]: currentPlan };
          }, pre);
        }

        return {
          ...pre,
          [planCode]: currentPlan,
        };
      }, {})
      .value();
  }

  /**
   * @description For partner preload plan with pro Monthly/Annual
   * @param p {PartnerReferBean}
   */
  function replacePartnerPreloadList(p) {
    if (isEmptyVal(p?.preloadPlan)) return;

    p.preloadPlan = _.reduce(p.preloadPlan, (pre, planCode) => {
      const matchPlanList = _.find(PLAN_PRICE_CHANGE_UTILS.planCodeGroupList, (planCodeList) => _.includes(planCodeList, planCode));

      if (!isEmptyVal(matchPlanList)) {
        return [...pre, ...matchPlanList];
      }

      return [...pre, planCode];
    }, []);
  }

  /**
   * @description need to be compatible with the one-dimensional array structure returned by the backend of the old version
   * @description need auto add zoom one pro Monthly/Annual group plan list
   * @description [['yearly_c50', 'masterclass']] -> [['yearly_c50', 'masterclass'], ['zo_pro_yearly', 'masterclass']]
   * @param p {PartnerReferBean}
   */
  function replacePartnerEligiblePlans(p) {
    if (isEmptyVal(p?.rules?.eligiblePlanList)) return;

    // [['yearly_c50', 'masterclass']] -> [['yearly_c50', 'masterclass'], ['zo_pro_yearly', 'masterclass']]
    p.rules.eligiblePlanList = _.chain(p?.rules?.eligiblePlanList)
      .filter(Boolean)
      .map((item) => _.isString(item) ? [item] : item)
      .reduce((pre = [], planList = []) => {
        const matchProPlanCodeList = _.find(PLAN_PRICE_CHANGE_UTILS.planCodeGroupList, (planCodeList) => {
          return _.some(planCodeList, (planCode) => _.includes(planList, planCode));
        });

        // match increase plan cases, manually add other plan
        if (matchProPlanCodeList) {
          const matchPlanCode = _.find(matchProPlanCodeList, (p) => _.includes(planList, p));
          const otherPlanCode = _.find(matchProPlanCodeList, (p) => p !== matchPlanCode);
          const otherPlanList = _.reduce(planList, (v, p) => {
            return [...v, p === matchPlanCode ? otherPlanCode : p];
          }, []);

          return [...pre, planList, otherPlanList];
        }

        return [...pre, planList];
      }, [])
      .value();
  }

  /** @type {PartnerReferBean} */
  const partnerReferral = config?.partnerReferral;

  if (isEmptyVal(partnerReferral)) return config;
  if (isEmptyVal(partnerReferral?.rules)) partnerReferral.rules = {};

  const { messageMap } = partnerReferral || {};
  const couponJsonString = config?.marketingCoupon;

  // will use marketingCoupon fieldValue to set refer bind with coupon node
  if (!isEmptyVal(couponJsonString)) {
    const couponData = safeJSONParse(couponJsonString);
    if (couponData) {
      couponData.defaultDiscountInfo = transferPlanCodeListToDiscountInfo(couponData?.defaultPlanCodeList);
    }
    partnerReferral.coupon = couponData;
    config.marketingCoupon = null;
  }

  // Merge the fields in the message map into partner
  if (!isEmptyVal(messageMap)) {
    _.assign(partnerReferral, messageMap);
    delete partnerReferral.messageMap;
  }

  // PRO INCREASE
  replacePartnerPreloadList(partnerReferral);
  replacePartnerPlanPrice(partnerReferral);
  replacePartnerEligiblePlans(partnerReferral);

  logger.info("parse partnerReferral", partnerReferral);

  return {
    ...config,
    partnerReferral,
  };
};
